/**
 * Local-mode login against localauth0 (ONE-7804).
 *
 * WHY THIS EXISTS INSTEAD OF `loginWithRedirect()`
 * ------------------------------------------------
 * localauth0 does implement `/authorize` + the authorization_code grant, but
 * the id_token it returns cannot pass `@auth0/auth0-spa-js`'s OIDC validation:
 *
 *   - `aud` is set to the API audience (`https://api.hogc.local`), while the
 *     SDK requires `aud === clientId` (see auth0-spa-js `src/jwt.ts`).
 *   - the SDK always generates a `nonce` and requires it echoed in the
 *     id_token, but its token-exchange body carries no `nonce`, so localauth0
 *     mints `nonce: null`.
 *
 * Either one alone makes `handleRedirectCallback()` throw. So in local mode we
 * obtain a token directly instead of running the browser redirect ceremony.
 *
 * This is NOT an auth bypass. The token below is a real RS256 JWT signed by
 * localauth0, and the API validates it against localauth0's real JWKS with no
 * environment-specific branch. What is skipped is only the redirect ceremony,
 * which is Auth0-tenant-specific and cannot be reproduced faithfully by
 * localauth0 anyway.
 *
 * See docs/adr/0003-local-login-mints-tokens-directly.md.
 *
 * TRANSPORT: calls go to the CORS shim (VUE_APP_LOCALAUTH0_URL, host 37302),
 * not localauth0's own port. localauth0 registers no CORS middleware, so the
 * browser could neither read its responses nor pass the preflight that the
 * application/json admin calls trigger. See the API repo's docker-compose.
 */

const CLAIM_NAMESPACE = 'https://heartofgodchurch.org';
export const MEMBER_ID_CLAIM = `${CLAIM_NAMESPACE}/member_id`;
export const NICKNAME_CLAIM = `${CLAIM_NAMESPACE}/nickname`;
export const EMAIL_VERIFIED_CLAIM = `${CLAIM_NAMESPACE}/email_verified`;

/**
 * localauth0 0.9.1 validates these literal values. Its README suggests
 * otherwise; the literals are what actually work.
 */
const CLIENT_ID = 'client_id';
const CLIENT_SECRET = 'client_secret';

const jsonPost = async (fetchImpl, url, body) => {
  const response = await fetchImpl(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`localauth0 ${url} failed: ${response.status} ${detail}`);
  }
  return response;
};

/**
 * Mint a real localauth0 access token carrying the given persona's identity.
 *
 * localauth0's identity and custom claims are GLOBAL container state — setting
 * them replaces the previous values for every subsequently-issued token. That
 * is fine for single-developer local use, but it means two personas must not be
 * minted concurrently. Callers should await this before starting another.
 *
 * @param {import('../config/local-personas').LocalPersona} persona
 * @param {Object} [options]
 * @param {typeof fetch} [options.fetchImpl] Injectable for tests.
 * @param {string} [options.baseUrl] Defaults to VUE_APP_LOCALAUTH0_URL.
 * @param {string} [options.audience] Defaults to VUE_APP_AUTH0_AUDIENCE.
 * @returns {Promise<string>} A real RS256 access token.
 */
export async function mintPersonaToken(persona, options = {}) {
  const {
    fetchImpl = fetch,
    baseUrl = process.env.VUE_APP_LOCALAUTH0_URL,
    audience = process.env.VUE_APP_AUTH0_AUDIENCE,
  } = options;

  if (!persona) throw new Error('mintPersonaToken: persona is required');
  if (!baseUrl) {
    throw new Error(
      'mintPersonaToken: VUE_APP_LOCALAUTH0_URL is not set. Copy .env.local.example to .env.local.',
    );
  }

  const root = baseUrl.replace(/\/$/, '');

  // 1. Identity shown in userinfo / id_token.
  await jsonPost(fetchImpl, `${root}/oauth/token/user_info`, {
    subject: `auth0|${persona.username}`,
    name: persona.label,
    nickname: persona.username,
    email: persona.email,
    email_verified: true,
  });

  // 2. The namespaced claims the API guard actually reads. A POST replaces the
  //    whole set, so all three must be sent together every time.
  await jsonPost(fetchImpl, `${root}/oauth/token/custom_claims`, {
    custom_claims: [
      { name: MEMBER_ID_CLAIM, value: { String: String(persona.memberId) } },
      { name: NICKNAME_CLAIM, value: { String: persona.username } },
      { name: EMAIL_VERIFIED_CLAIM, value: { String: 'true' } },
    ],
  });

  // 3. Mint. The audience must be declared in the API repo's localauth0.toml or
  //    localauth0 answers `access_denied`.
  const tokenResponse = await jsonPost(fetchImpl, `${root}/oauth/token`, {
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    audience,
    grant_type: 'client_credentials',
  });

  const body = await tokenResponse.json();
  if (!body?.access_token) {
    throw new Error(`localauth0 token response missing access_token: ${JSON.stringify(body)}`);
  }
  return body.access_token;
}
